/**
 * Créditos dos dados que estão no mapa, para o "Sobre".
 *
 * Saem das próprias camadas do OpenLayers (`attributions` de cada fonte) — os
 * mesmos textos que o botão "i" do canto do mapa mostrava, inclusive os que
 * vêm com o dado e que as licenças pedem junto dele (AIS das embarcações,
 * Copernicus/CAMS, PurpleAir). Só as camadas visíveis contam.
 */
import type { FrameState } from 'ol/Map';
import type OlMap from 'ol/Map';

/** Crédito em HTML (`<a href>texto</a>`) → texto puro, sem espaços sobrando. */
export function textoDoCredito(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&copy;/g, '©')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Junta os créditos, sem vazios nem repetidos, na ordem em que aparecem. */
export function juntarCreditos(listas: readonly (string | readonly string[] | null | undefined)[]): string[] {
  const vistos = new Set<string>();
  for (const lista of listas) {
    for (const item of typeof lista === 'string' ? [lista] : (lista ?? [])) {
      const texto = textoDoCredito(item);
      if (texto) vistos.add(texto);
    }
  }
  return [...vistos];
}

/** Os créditos das camadas visíveis num quadro do mapa. */
export function creditosDoQuadro(map: OlMap, frameState: FrameState): string[] {
  const listas: (string | string[] | null)[] = [];
  const camadas = [...map.getAllLayers()].sort((a, b) => (a.getZIndex() ?? 0) - (b.getZIndex() ?? 0));
  for (const camada of camadas) {
    if (!camada.isVisible(map.getView())) continue;
    const fn = camada.getSource()?.getAttributions();
    if (!fn) continue;
    try {
      // O tipo do OL pede a extensão preenchida; num quadro desenhado ela está.
      listas.push(fn(frameState as Parameters<typeof fn>[0]));
    } catch {
      // crédito de uma camada não pode derrubar o diálogo
    }
  }
  return juntarCreditos(listas);
}

/** Pede um quadro ao mapa e devolve os créditos das camadas visíveis nele. */
export function creditosNoMapa(map: OlMap): Promise<string[]> {
  return new Promise((resolve) => {
    let feito = false;
    const fim = (creditos: string[]) => {
      if (feito) return;
      feito = true;
      clearTimeout(limite);
      resolve(creditos);
    };
    // Mapa sem tamanho (aba escondida) não desenha: não deixa o diálogo esperando.
    const limite = setTimeout(() => fim([]), 1500);
    map.once('postrender', (evento) => fim(evento.frameState ? creditosDoQuadro(map, evento.frameState) : []));
    map.render();
  });
}
